import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSearchbar,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonChip,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import {
  calendarOutline,
  locationOutline,
  addOutline,
  checkmarkCircle,
  closeOutline,
  createOutline,
  footballOutline,
} from 'ionicons/icons';
import { format } from 'date-fns';
import { Event } from '../types';
import CreateEventForm from './CreateEventForm';
import { useTranslation } from 'react-i18next';
import { useEvent } from '../contexts/EventContext';
import { premierLeagueService, Match } from '../services/premierLeague';
import { eventService } from '../services/api';
import { createMatchName } from '../utils/teamUtils';

interface EventSelectOrCreateProps {
  selectedEvent?: Event | null;
  onEventSelect: (event: Event) => void;
  showCreateInline?: boolean;
}

const EventSelectOrCreate: React.FC<EventSelectOrCreateProps> = ({
  selectedEvent,
  onEventSelect,
  showCreateInline = true,
}) => {
  const { t } = useTranslation();
  const { events, refreshEvents } = useEvent();
  const [searchText, setSearchText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mode, setMode] = useState<'select' | 'create'>('select');

  const filteredEvents = events.filter((event) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      event.name.toLowerCase().includes(searchLower) ||
      event.venue.toLowerCase().includes(searchLower) ||
      event.city?.toLowerCase().includes(searchLower)
    );
  });

  const getEventStatus = (event: Event): string => {
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (event.status === 'cancelled') return 'cancelled';
    if (eventDate < today) return 'past';
    return 'active';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'past':
        return 'medium';
      case 'cancelled':
        return 'danger';
      default:
        return 'primary';
    }
  };

  return (
    <div>
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setMode('select')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center ${
            mode === 'select'
              ? 'bg-purple-500 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
          }`}
        >
          <IonIcon icon={checkmarkCircle} className="mr-2" />
          {t('events.selectEvent')}
        </button>
        <button
          onClick={() => setMode('create')}
          className={`flex-1 py-2.5 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center ${
            mode === 'create'
              ? 'bg-purple-500 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
          }`}
        >
          <IonIcon icon={addOutline} className="mr-2" />
          {t('events.createEvent')}
        </button>
      </div>

      {mode === 'select' ? (
        <>
          {/* Selected Event Display */}
          {selectedEvent && (
            <IonCard className="mb-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-300 dark:border-purple-700">
              <IonCardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {selectedEvent.imageUrl && (
                      <IonAvatar className="w-12 h-12">
                        <img src={selectedEvent.imageUrl} alt={selectedEvent.name} />
                      </IonAvatar>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg">{selectedEvent.name}</h3>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p className="flex items-center">
                          <IonIcon icon={calendarOutline} className="mr-1" />
                          {format(new Date(selectedEvent.date), 'MMM dd, yyyy')}
                          {selectedEvent.time && ` • ${selectedEvent.time}`}
                        </p>
                        <p className="flex items-center">
                          <IonIcon icon={locationOutline} className="mr-1" />
                          {selectedEvent.venue}
                        </p>
                      </div>
                    </div>
                  </div>
                  <IonChip color={getStatusColor(getEventStatus(selectedEvent))}>
                    <IonLabel className="text-xs font-medium">
                      {t(`events.status.${getEventStatus(selectedEvent)}`)}
                    </IonLabel>
                  </IonChip>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Search Bar */}
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value!)}
            placeholder={t('events.searchEvents')}
            className="mb-3 p-0"
          />

          {/* Events List */}
          <div className="max-h-80 overflow-y-auto">
            <IonList className="bg-transparent">
              {filteredEvents.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400 mb-3">
                    {searchText ? t('common.noResultsFound') : t('events.noEvents')}
                  </p>
                  <IonButton size="small" onClick={() => setShowCreateModal(true)}>
                    <IonIcon icon={addOutline} slot="start" />
                    {t('events.createEvent')}
                  </IonButton>
                </div>
              ) : (
                filteredEvents.map((event) => {
                  const status = getEventStatus(event);
                  const isSelected = selectedEvent?._id === event._id;
                  
                  return (
                    <IonItem
                      key={event._id}
                      button
                      onClick={() => onEventSelect(event)}
                      className={`mb-2 rounded-lg ${
                        isSelected
                          ? 'bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-500'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                    >
                      {event.imageUrl && (
                        <IonAvatar slot="start" className="w-10 h-10">
                          <img src={event.imageUrl} alt={event.name} />
                        </IonAvatar>
                      )}
                      <IonLabel>
                        <h3 className="font-medium">{event.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {format(new Date(event.date), 'MMM dd, yyyy')} • {event.venue}
                        </p>
                      </IonLabel>
                      <IonChip slot="end" color={getStatusColor(status)}>
                        <IonLabel className="text-xs">
                          {t(`events.status.${status}`)}
                        </IonLabel>
                      </IonChip>
                      {isSelected && (
                        <IonIcon
                          icon={checkmarkCircle}
                          slot="end"
                          className="text-purple-500 text-xl ml-2"
                        />
                      )}
                    </IonItem>
                  );
                })
              )}
            </IonList>
          </div>

          {/* Create New Event Button */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <IonButton
              expand="block"
              fill="outline"
              onClick={() => setShowCreateModal(true)}
            >
              <IonIcon icon={addOutline} slot="start" />
              {t('events.createEvent')}
            </IonButton>
          </div>
        </>
      ) : (
        /* Inline Create Event Form */
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <CreateEventForm
            onClose={() => setMode('select')}
            onSuccess={async () => {
              await refreshEvents();
              setMode('select');
              // Auto-select the newly created event
              const latestEvent = events[events.length - 1];
              if (latestEvent) {
                onEventSelect(latestEvent);
              }
            }}
          />
        </div>
      )}

      {/* Create Event Modal */}
      <IonModal
        isOpen={showCreateModal}
        onDidDismiss={() => setShowCreateModal(false)}
        className="event-modal-fullscreen"
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('events.createEvent')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowCreateModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <CreateEventForm
            onClose={() => setShowCreateModal(false)}
            onSuccess={async () => {
              setShowCreateModal(false);
              await refreshEvents();
              // Auto-select the newly created event
              const latestEvent = events[events.length - 1];
              if (latestEvent) {
                onEventSelect(latestEvent);
              }
            }}
          />
        </IonContent>
      </IonModal>
    </div>
  );
};

export default EventSelectOrCreate;